// Lo PURO de las planillas en el chat: cómo el agente las ENTIENDE y cómo escribe una
// nueva — ADR-2026-09-08-el-agente-lee-y-escribe-planillas.
//
// El problema central es el tamaño. Una planilla de trabajo tiene 5.000 filas y meterla
// entera en el turno es imposible: no entra, y si entrara se arrastraría en todo el
// historial. Así que al adjuntarla viaja su FORMA (hojas, columnas, cuántas filas) más
// una muestra de las primeras, y el agente pide los tramos que necesite con `ver_planilla`
// — el mismo patrón que `ver_fotos` para las fotos en montón.
//
// La forma sola ya alcanza para la mayoría de los pedidos: para "sacá la columna precio" o
// "ordenalas por stock" no hace falta ver las 5.000 filas, hace falta saber qué columnas
// hay.

import { aCsv } from '../../../utils/planilla/csv';
import type { HojaPlanilla } from '../../../utils/planilla/xlsx';

export interface Planilla {
  /** Nombre del archivo, con extensión. */
  nombre: string;
  hojas: HojaPlanilla[];
}

/** Filas de muestra que viajan al adjuntar. Suficiente para ver la pinta de los datos. */
export const FILAS_MUESTRA = 20;
/** Filas por llamada a `ver_planilla`. */
export const MAX_FILAS_POR_LECTURA = 200;
/** Columnas que se muestran. Más que esto no se lee ni se razona: se nombra el resto. */
export const MAX_COLUMNAS = 30;
/** Tope de celdas de una planilla CREADA por el agente (filas × columnas de todas las
 *  hojas). Es lo que entra razonablemente en un turno de tool. */
export const MAX_CELDAS_CREAR = 20_000;
/** Tope de filas que se guardan de un archivo adjunto. */
export const MAX_FILAS_LEIDAS = 20_000;

const texto = (v: unknown): string =>
  typeof v === 'string' ? v : typeof v === 'number' || typeof v === 'boolean' ? String(v) : '';

/** Cuántas columnas tiene la hoja: el máximo de todas sus filas, no el de la primera. */
export const anchoDeHoja = (hoja: HojaPlanilla): number =>
  hoja.filas.reduce((max, f) => Math.max(max, f.length), 0);

/**
 * Los encabezados de una hoja. Se asume que la primera fila lo es —es lo que pasa en el
 * 99% de las planillas— pero se numera lo que esté vacío para que ninguna columna quede
 * sin nombre y el agente pueda referirse a ella.
 */
export function encabezados(hoja: HojaPlanilla): string[] {
  const ancho = Math.min(anchoDeHoja(hoja), MAX_COLUMNAS);
  const primera = hoja.filas[0] ?? [];
  return Array.from({ length: ancho }, (_, i) => (primera[i] || '').trim() || `Columna ${i + 1}`);
}

/** Una grilla como tabla de texto separada por ` | `. Compacta y sin ambigüedad. */
export function filasComoTexto(filas: string[][], desde1Based: number): string {
  return filas
    .map((f, i) => {
      const celdas = f.slice(0, MAX_COLUMNAS).map((c) => (c ?? '').replace(/\s+/g, ' ').trim());
      return `${desde1Based + i}: ${celdas.join(' | ')}`;
    })
    .join('\n');
}

/**
 * Lo que viaja en el turno al adjuntar: qué hay adentro y cómo se ve, sin el contenido
 * entero. Es lo que el agente lee para saber si necesita pedir más.
 */
export function resumenDePlanilla(p: Planilla): string {
  const partes: string[] = [
    `[Planilla adjunta: ${p.nombre}] ${p.hojas.length} hoja(s). No la ves entera: acá va la FORMA y una muestra. Para leer más filas, llamá ver_planilla.`,
  ];
  for (const hoja of p.hojas) {
    const ancho = anchoDeHoja(hoja);
    const cols = encabezados(hoja);
    const sobran = ancho > MAX_COLUMNAS ? ` (+${ancho - MAX_COLUMNAS} columnas más, no se muestran)` : '';
    partes.push(
      `\nHoja "${hoja.nombre}": ${hoja.filas.length} fila(s) × ${ancho} columna(s).` +
        `\nColumnas: ${cols.join(' | ')}${sobran}`,
    );
    const muestra = hoja.filas.slice(1, 1 + FILAS_MUESTRA);
    if (muestra.length > 0) {
      partes.push(
        `Primeras ${muestra.length} fila(s) de datos (el número es la fila real de la planilla):\n${filasComoTexto(muestra, 2)}`,
      );
      const restantes = hoja.filas.length - 1 - muestra.length;
      if (restantes > 0) partes.push(`(quedan ${restantes} filas sin mostrar)`);
    } else if (hoja.filas.length <= 1) {
      partes.push('(sin filas de datos)');
    }
  }
  return partes.join('\n');
}

// ── ver_planilla ────────────────────────────────────────────────────────────

export interface VentanaFilas {
  /** Índice de la hoja pedida, ya acotado. */
  hoja: number;
  /** Primera fila, 1-based (la 1 es el encabezado). */
  desde: number;
  cantidad: number;
}

const entero = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isFinite(n) ? Math.round(n) : null;
};

/**
 * Qué tramo pidió el modelo. La hoja se puede pedir por número (1-based) o por nombre;
 * lo que no matchea cae a la primera, que es lo que quiere el 99% de las veces.
 */
export function ventanaDeFilas(p: Planilla | null, entrada: unknown): VentanaFilas {
  const o = (entrada ?? {}) as { hoja?: unknown; desde?: unknown; filas?: unknown };
  let hoja = 0;
  if (p && p.hojas.length > 0) {
    const pedida = o.hoja;
    if (typeof pedida === 'string' && pedida.trim()) {
      const porNombre = p.hojas.findIndex(
        (h) => h.nombre.toLowerCase() === pedida.trim().toLowerCase(),
      );
      hoja = porNombre >= 0 ? porNombre : 0;
      if (porNombre < 0) {
        const n = entero(pedida);
        if (n !== null) hoja = Math.min(Math.max(n - 1, 0), p.hojas.length - 1);
      }
    } else {
      const n = entero(pedida);
      if (n !== null) hoja = Math.min(Math.max(n - 1, 0), p.hojas.length - 1);
    }
  }
  const desde = Math.max(entero(o.desde) ?? 2, 1);
  const cantidad = Math.min(Math.max(entero(o.filas) ?? 50, 1), MAX_FILAS_POR_LECTURA);
  return { hoja, desde, cantidad };
}

/** El texto que devuelve `ver_planilla`. */
export function lecturaDePlanilla(p: Planilla | null, v: VentanaFilas): string {
  if (!p) {
    return 'No hay ninguna planilla adjunta en este chat. Pedile al usuario que la adjunte con el "+".';
  }
  const hoja = p.hojas[v.hoja];
  if (!hoja) return `Esa hoja no existe. La planilla tiene ${p.hojas.length}.`;
  const total = hoja.filas.length;
  if (v.desde > total) {
    return `Ese tramo no existe: la hoja "${hoja.nombre}" tiene ${total} fila(s). Pedí un \`desde\` entre 1 y ${total}.`;
  }
  const filas = hoja.filas.slice(v.desde - 1, v.desde - 1 + v.cantidad);
  const hasta = v.desde + filas.length - 1;
  const quedan = total - hasta;
  return [
    `Hoja "${hoja.nombre}", filas ${v.desde} a ${hasta} de ${total}. Columnas: ${encabezados(hoja).join(' | ')}`,
    filasComoTexto(filas, v.desde),
    quedan > 0
      ? `(quedan ${quedan} filas; para seguir, ver_planilla con desde=${hasta + 1})`
      : '(es el final de la hoja)',
  ].join('\n');
}

// ── crear_planilla ──────────────────────────────────────────────────────────

export type FormatoPlanilla = 'xlsx' | 'csv';

export interface PlanillaCreada {
  nombre: string;
  formato: FormatoPlanilla;
  hojas: HojaPlanilla[];
  /** Celdas que quedaron afuera por el tope. 0 = entró todo. */
  recortadas: number;
}

/** Nombre de archivo seguro, sin barras ni extensión duplicada. */
export function nombreDeArchivoPlanilla(nombre: unknown, formato: FormatoPlanilla): string {
  const base =
    texto(nombre)
      .replace(/\.(xlsx|xls|csv)$/i, '')
      .replace(/[/\\:*?"<>|]/g, '-')
      .trim()
      .slice(0, 80) || 'planilla';
  return `${base}.${formato}`;
}

/**
 * Lo que escribió el modelo → una planilla válida. Nunca tira: lo que no se entiende se
 * descarta y lo que se pasa del tope se recorta avisando, porque un error acá dejaría al
 * usuario sin archivo después de que el agente le dijo que se lo estaba armando.
 *
 * Todas las celdas se pasan a texto: el modelo manda números y booleanos sueltos, y el
 * escritor de xlsx ya decide solo qué va como número.
 */
export function normalizarPlanillaCreada(entrada: unknown): PlanillaCreada {
  const o = (entrada ?? {}) as {
    nombre?: unknown;
    formato?: unknown;
    hojas?: unknown;
  };
  const formato: FormatoPlanilla = texto(o.formato).toLowerCase() === 'csv' ? 'csv' : 'xlsx';
  const crudas = Array.isArray(o.hojas) ? o.hojas : [];
  const hojas: HojaPlanilla[] = [];
  let celdas = 0;
  let recortadas = 0;
  for (const [i, h] of crudas.entries()) {
    if (!h || typeof h !== 'object') continue;
    const ho = h as { nombre?: unknown; filas?: unknown };
    const filasCrudas = Array.isArray(ho.filas) ? ho.filas : [];
    const filas: string[][] = [];
    for (const f of filasCrudas) {
      if (!Array.isArray(f)) continue;
      const fila = f.slice(0, MAX_COLUMNAS).map(texto);
      if (celdas + fila.length > MAX_CELDAS_CREAR) {
        recortadas += fila.length;
        continue;
      }
      celdas += fila.length;
      filas.push(fila);
    }
    hojas.push({ nombre: texto(ho.nombre) || `Hoja ${i + 1}`, filas });
  }
  return {
    nombre: nombreDeArchivoPlanilla(o.nombre, formato),
    formato,
    hojas: hojas.length > 0 ? hojas : [{ nombre: 'Hoja 1', filas: [] }],
    recortadas,
  };
}

/** Cuántas celdas tiene, para mostrarlo y para el resumen al agente. */
export const contarCeldas = (hojas: HojaPlanilla[]): number =>
  hojas.reduce((n, h) => n + h.filas.reduce((m, f) => m + f.length, 0), 0);

/** El CSV de una planilla creada. Con varias hojas sólo entra la primera: un csv es UNA
 *  tabla, y pegarlas todas juntas daría un archivo que no abre bien en ningún lado. */
export const csvDePlanilla = (p: PlanillaCreada): string => aCsv(p.hojas[0]?.filas ?? []);

/** Lo que vuelve al agente cuando la card ya escribió el archivo. */
export function resumenPlanillaParaAgente(p: PlanillaCreada): {
  created: true;
  nombre: string;
  formato: FormatoPlanilla;
  hojas: Array<{ nombre: string; filas: number }>;
  recortadas: number;
} {
  return {
    created: true,
    nombre: p.nombre,
    formato: p.formato,
    hojas: p.hojas.map((h) => ({ nombre: h.nombre, filas: h.filas.length })),
    recortadas: p.recortadas,
  };
}

/** El `[registro:]` del historial: el agente puede referirse después al archivo que hizo. */
export function registroPlanillaParaHistorial(p: PlanillaCreada): string {
  const detalle = p.hojas.map((h) => `"${h.nombre}" (${h.filas.length} filas)`).join(', ');
  const recorte =
    p.recortadas > 0 ? ` Se recortaron ${p.recortadas} celdas por el tope de tamaño.` : '';
  return `[registro: armaste la planilla "${p.nombre}" con ${detalle}. Ya está lista para descargar en el chat; el usuario la baja cuando quiera.${recorte}]`;
}
